import { Box, IconButton, Toolbar, Tooltip, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar } from './Appbar'
import { GlobalContext } from 'GlobalContext'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import SearchIcon from '@mui/icons-material/Search'
import { Left } from './Left'
import { Right } from './Right'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { ChevronRight } from '@mui/icons-material';
import { AppearanceDrawer } from './AppearanceDrawer';
import { ReactComponent as BrandLogo } from 'assets/imagotipo.svg'
import { useTheme } from '@mui/material/styles'
import { pushNotificationService } from 'services/PushNotificationService';

interface NavbarProps {
    view: {
        title: string;
        component: React.ReactNode;
    };
    right: {
        title: string;
        component: React.ReactNode;
    };
}

const Navbar: React.FC<NavbarProps> = ({ view, right }) => {
    const { openLeft, openRight, handlerSideLeft, handlerSideRight } = useContext(GlobalContext)
    const [openAppearanceDrawer, setOpenAppearanceDrawer] = useState(false)
    const theme = useTheme()
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
    const shortcutLabel = isMac ? 'Cmd+K' : 'Ctrl+K'
    const logoStyles = {
        width: '170px',
        maxWidth: '100%',
        height: '40px',
        '--letters-fill': theme.palette.mode === 'dark' ? '#ffffff' : '#121212',
        '--background-fill': theme.palette.primary.main,
    } as React.CSSProperties

    const handleOpenAppearanceDrawer = (event: React.MouseEvent<HTMLButtonElement>) => {
        handlerSideRight(false)
        setOpenAppearanceDrawer(true)
        event.currentTarget.blur()
    }

    const [notificationsEnabled, setNotificationsEnabled] = useState(false);

    useEffect(() => {
        pushNotificationService.getCurrentSubscription().then((sub) => {
            setNotificationsEnabled(sub !== null);
        });
    }, []);

    const handleNotificationToggle = async () => {
        if (!notificationsEnabled) {
            const result = await pushNotificationService.subscribe();
            setNotificationsEnabled(result !== null);
        }
    };

    const handleSearchClick = () => {
        // Simula el atajo de la paleta (Cmd+K en macOS, Ctrl+K en otros sistemas)
        const event = new KeyboardEvent('keydown', {
            key: 'k',
            code: 'KeyK',
            ctrlKey: !isMac,
            metaKey: isMac,
            bubbles: true
        });
        document.dispatchEvent(event);
    }

    return (
        <>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <AppBar
                    position='fixed'
                    open={openLeft || openRight}
                    left={openLeft ? 240 : 0}
                    right={openRight ? 320 : 0}
                    sx={{
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        boxShadow: 'none',
                        transition: 'filter 0.2s ease, opacity 0.2s ease',
                        ...(openAppearanceDrawer && {
                            filter: 'blur(2px)',
                            opacity: 0.9,
                        }),

                    }}
                >
                    <Toolbar sx={{
                        color: (theme) => theme.palette.text.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: (theme) => theme.palette.background.paper,
                        paddingLeft: 0,
                    }}>
                        {/* Sección izquierda */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                            {/* <IconButton
                                color="inherit"
                                onClick={() => handlerSideLeft(true)}
                                edge="start"
                                sx={{
                                    marginRight: 2,
                                    ...(openLeft && { display: 'none' }),
                                }}
                            >
                                <MenuIcon />
                            </IconButton> */}

                            {/* <Typography variant="h6" noWrap component="div">
                                {view.title}
                            </Typography> */}
                            <IconButton onClick={() => handlerSideLeft(!openLeft)}>
                                {openLeft ? <ChevronLeftIcon /> : <ChevronRight />}
                            </IconButton>
                        </Box>

                        {!openLeft && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 56,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: { xs: 'none', md: 'flex' },
                                    alignItems: 'center',
                                    zIndex: 1,
                                }}
                            >
                                <BrandLogo aria-label='logo-app-header-collapsed' style={logoStyles} />
                            </Box>
                        )}

                        {/* Buscador centrado */}
                        <Box
                            onClick={handleSearchClick}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                padding: { xs: '8px', md: '6px 12px' },
                                borderRadius: '6px',
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                backgroundColor: (theme) => theme.palette.action.hover,
                                cursor: 'pointer',
                                minWidth: { xs: 'auto', md: '300px' },
                                maxWidth: { xs: 'auto', md: '500px' },
                                transition: 'all 0.2s',
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.action.selected,
                                    borderColor: (theme) => theme.palette.primary.main,
                                },
                                '@media (max-width: 900px)': {
                                    minWidth: 'auto',
                                    maxWidth: 'auto',
                                    padding: '8px',
                                    position: 'static',
                                    transform: 'none',
                                }
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    flexGrow: 1,
                                    color: 'text.secondary',
                                    display: { xs: 'none', md: 'block' },
                                    '@media (max-width: 900px)': {
                                        display: 'none'
                                    }
                                }}
                            >
                                ¿A dónde quieres ir?
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.disabled',
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    display: { xs: 'none', md: 'block' },
                                    '@media (max-width: 900px)': {
                                        display: 'none'
                                    }
                                }}
                            >
                                {shortcutLabel}
                            </Typography>
                        </Box>

                        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Botón notificaciones: solo visible si no están activadas */}
                            {!notificationsEnabled && (
                                <Tooltip title="Activar notificaciones">
                                    <IconButton
                                        color="inherit"
                                        onClick={handleNotificationToggle}
                                        sx={{ '&.Mui-focusVisible': { backgroundColor: 'transparent' } }}
                                    >
                                        <NotificationsNoneIcon />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {/* Botón apariencia */}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                    color="inherit"
                                    onClick={handleOpenAppearanceDrawer}
                                    edge="end"
                                    sx={{
                                        '&.Mui-focusVisible': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    <SettingsIcon />
                                </IconButton>
                            </Box>

                            <Box sx={{ flex: '0 0 auto' }}>
                                <IconButton
                                    color="inherit"
                                    onClick={() => handlerSideRight(true)}
                                    edge="end"
                                    sx={{
                                        ...(openRight && { display: 'none' }),
                                    }}
                                >
                                    <MenuOpenIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Toolbar>
                </AppBar>

                {openLeft ? <Left /> : null}

                {openRight ? <Right title={right.title} component={right.component} /> : null}

                <AppearanceDrawer
                    open={openAppearanceDrawer}
                    onClose={() => setOpenAppearanceDrawer(false)}
                />
            </Box>

            <Box
                sx={{
                    marginTop: '65px',
                    marginLeft: openLeft ? '240px' : '0px',
                    marginRight: openRight ? '320px' : '0px',
                    height: 'calc(100% - 64px)',
                    padding: '0px'
                }}
            >
                {view.component}
            </Box>
        </>
    )
}

export default Navbar
